/**
 * Room Validation Service
 * 
 * Validates room access using Cloudflare Worker API for secure server-side
 * passcode verification with rate limiting and bcrypt hashing.
 * The passcode is NEVER compared client-side to prevent timing attacks.
 */

import { decodeLinkHash } from './linkService';
import { captureException } from '../lib/telemetry/posthog/frontend';
import { parseConversationConfig } from '../lib/multimodal-contract';
import type {
  RoomAccess,
  InterviewSessionStatus,
  ProductResearch,
} from '../types';

/**
 * Raw error body returned by `/api/room/validate` on a non-ok response. Only
 * the `error` message is read.
 */
interface RoomValidateErrorResponse {
  error?: string;
  error_code?: string;
}

/**
 * Expected shape of the `/api/room/validate` success body. Mirrors the columns
 * the server returns and the frontend reads. Fields that the caller narrows at
 * runtime (typeof / Array.isArray / equality checks) are typed as `unknown` so
 * the existing narrowing remains the source of truth; the rest are typed to
 * match the `RoomAccess` fields they flow into.
 *
 * This is a trust-boundary assertion, not runtime validation: the values are
 * not re-checked here beyond what the original code already did.
 */
interface RoomValidateResponse {
  conversation_config?: unknown;
  valid?: boolean;
  error?: string;
  // Present on every valid response and assigned directly into the
  // non-optional RoomAccess fields, matching the original `any`-typed access.
  session_id: string;
  room_id: string;
  status: InterviewSessionStatus;
  target_name: string;
  goal: string;
  target_url?: string;
  details?: string;
  product_research?: ProductResearch;
  output_language?: string;
  interview_language?: string;
  video_enabled?: boolean;
  require_video?: boolean;
  screen_share_enabled?: boolean;
  require_name?: boolean;
  require_email?: boolean;
  enforce_language?: string | null;
  schedule_status?: 'open' | 'not_yet_open' | 'closed';
  opens_at?: string | null;
  closes_at?: string | null;
  show_holito_referral?: boolean;
  workspace_id?: string | null;
  workspace_logo_url?: string | null;
  workspace_icon_url?: string | null;
  workspace_accent_color?: string | null;
  field_count?: unknown;
  probing_depth?: unknown;
  embed_widget?: unknown;
  identity_collection?: string;
  allowed_origins?: unknown;
  completion_action?: unknown;
  completion_message?: unknown;
  completion_redirect_url?: unknown;
}

/**
 * Error thrown on expected validation outcomes (wrong passcode, rate limit,
 * known API errors). The outer catch skips PostHog capture for these; only
 * genuinely unexpected failures (network, parse) are captured.
 */
interface KnownFlowError extends Error {
  _knownFlow?: true;
}

/** Mark an error as expected control flow so the outer catch skips capture. */
function markKnownFlow(error: Error): Error {
  (error as KnownFlowError)._knownFlow = true;
  return error;
}

/** True when the error was marked as expected control flow. */
function isKnownFlow(error: unknown): boolean {
  return error instanceof Error && (error as KnownFlowError)._knownFlow === true;
}

/**
 * Validate room access via the Cloudflare Pages Function (rate-limited,
 * bcrypt-verified server-side).
 *
 * @param roomId - 8-char room code.
 * @param passcode - Legacy 6-digit or new 8-char passcode.
 * @param turnstileToken - Optional Cloudflare Turnstile token. Forwarded to
 *   the endpoint only when present; absent keeps the frictionless flow
 *   (server enforces Turnstile only when a secret is configured).
 */
export async function validateRoomAccess(
  roomId: string,
  passcode: string,
  turnstileToken?: string,
): Promise<RoomAccess | null> {
  try {
    const body: { room_id: string; passcode: string; turnstile_token?: string } = {
      room_id: roomId,
      passcode,
    };
    if (turnstileToken) {
      body.turnstile_token = turnstileToken;
    }
    const response = await fetch('/api/room/validate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (response.status === 429) {
      throw markKnownFlow(new Error('Too many attempts. Please try again later.'));
    }

    if (!response.ok) {
      const errorData = (await response
        .json()
        .catch(() => ({ error: 'Unknown validation error' }))) as RoomValidateErrorResponse;
      if (errorData.error_code === 'conversation_unavailable') {
        throw markKnownFlow(new Error('This conversation is not available yet. Contact the person who shared this link.'));
      }
      const validationError = new Error(errorData.error || 'Unknown validation error');
      captureException(validationError, 'roomValidation.apiError', {
        roomId,
        statusCode: response.status,
        errorMessage: errorData.error,
      });
      // Already captured here; mark so the outer catch does not double-capture.
      throw markKnownFlow(validationError);
    }

    const data = (await response.json()) as RoomValidateResponse;

    // Schedule-blocked responses return valid: false with schedule_status
    // Return a partial RoomAccess so the frontend can display schedule info
    if (!data.valid && data.schedule_status) {
      return {
        sessionId: '',
        roomId: roomId,
        status: 'active' as const,
        target_name: '',
        goal: '',
        videoEnabled: false,
        // Campaign schedule info for the frontend
        schedule_status: data.schedule_status,
        opens_at: data.opens_at ?? null,
        closes_at: data.closes_at ?? null,
      };
    }

    if (!data.valid) {
      throw markKnownFlow(new Error(data.error || 'Invalid room ID or passcode'));
    }

    const conversationConfig = parseConversationConfig(data.conversation_config);
    if (data.conversation_config != null && !conversationConfig) {
      throw new Error('Conversation unavailable');
    }

    return {
      ...(conversationConfig ? { conversation_config: conversationConfig } : {}),
      sessionId: data.session_id,
      roomId: data.room_id,
      status: data.status,
      // Interview configuration from direct columns
      target_name: data.target_name,
      target_url: data.target_url,
      goal: data.goal,
      details: data.details,
      product_research: data.product_research,
      output_language: data.output_language,
      // Persisted language from resumed sessions
      interview_language: data.interview_language,
      videoEnabled: data.video_enabled ?? false,
      requireVideo: data.require_video ?? false,
      screenShareEnabled: data.screen_share_enabled ?? false,
      // Campaign settings for multi-response
      require_name: data.require_name,
      require_email: data.require_email,
      enforce_language: data.enforce_language ?? null,
      // Campaign schedule
      schedule_status: data.schedule_status,
      opens_at: data.opens_at,
      closes_at: data.closes_at,
      // Org referral setting
      show_holito_referral: data.show_holito_referral,
      // Workspace branding (wide logo + square icon for the live interview surfaces)
      workspaceId: data.workspace_id ?? null,
      workspaceLogoUrl: data.workspace_logo_url ?? null,
      workspaceIconUrl: data.workspace_icon_url ?? null,
      workspaceAccentColor: data.workspace_accent_color ?? null,
      // Inputs for the join-screen time estimate. Schema content stays on the server.
      field_count: typeof data.field_count === 'number' ? data.field_count : undefined,
      probing_depth: typeof data.probing_depth === 'number' ? data.probing_depth : undefined,
      // Embed entitlement (used by /embed route to refuse render when workspace plan lacks it).
      embed_widget: data.embed_widget === true,
      // Identity collection mode (pre, post, never) drives PreInterviewForm
      // skipping and the post-call identity step in both direct-link and
      // embed flows.
      identity_collection:
        data.identity_collection === 'post' ||
        data.identity_collection === 'never' ||
        data.identity_collection === 'pre'
          ? data.identity_collection
          : 'pre',
      // Per-campaign embed allowlist (NULL/empty = no restriction). The
      // iframe-side launcher/join shell uses this with `matchesAllowedOrigin`
      // to refuse rendering when the parent page is not on the list.
      allowed_origins: Array.isArray(data.allowed_origins) ? data.allowed_origins : null,
      // Post-call behaviour. 'redirect' is gated by the completion_redirect
      // entitlement at save-time; if it lapses the SuccessScreen still
      // re-validates the URL before navigating.
      completion_action: data.completion_action === 'redirect' ? 'redirect' : 'message',
      completion_message: typeof data.completion_message === 'string' ? data.completion_message : null,
      completion_redirect_url: typeof data.completion_redirect_url === 'string'
        ? data.completion_redirect_url
        : null,
      // BP-6: interviewee fields are per-response, not returned from room validation
    };
  } catch (error) {
    console.error('Error validating room access:', error);
    // Capture only genuinely unexpected failures (network errors, parse errors).
    // Known flow errors (rate limit, wrong passcode, known API errors) are either
    // not captured or already captured at their throw site.
    if (!isKnownFlow(error)) {
      captureException(error instanceof Error ? error : new Error(String(error)), 'roomValidation.exception', { roomId });
    }
    throw error;
  }
}

/**
 * Validate room access from a link hash. Decodes the reversible
 * base64(roomId:passcode) payload and delegates to {@link validateRoomAccess}.
 *
 * @param hash - The link hash from the share URL.
 * @param turnstileToken - Optional Turnstile token, forwarded when present.
 */
export async function validateRoomAccessByHash(
  hash: string,
  turnstileToken?: string,
): Promise<RoomAccess | null> {
  const decoded = decodeLinkHash(hash);
  if (!decoded) return null;
  return validateRoomAccess(decoded.roomId, decoded.passcode, turnstileToken);
}

/** Public room info, or a transient-server-error signal. */
export interface RoomPublicInfo {
  /** True only when the server confirmed the room exists. */
  exists: boolean;
  status?: string;
  targetName?: string;
  interviewLanguage?: string;
  hasStarted?: boolean;
  /** Workspace ID owning this session, when active and branded. */
  workspaceId?: string | null;
  /** Wide-logo URL for the join + on-call header. Null when no logo set. */
  workspaceLogoUrl?: string | null;
  /** Square-icon URL for the animated workspace loader. Null when no icon set. */
  workspaceIconUrl?: string | null;
  /** Space accent for the respondent theme. */
  workspaceAccentColor?: string | null;
  /**
   * True when the status check could not be completed because of a transient
   * server/network failure (5xx, fetch reject) AFTER retries were exhausted.
   * The caller must NOT treat this as "room does not exist" (A10): a transient
   * 5xx is a verification failure, not a missing room. `exists` is `false` in
   * this case purely because existence is unknown.
   */
  serverError?: boolean;
}

/** Max attempts (1 initial + retries) for the room-status fetch on transient 5xx. */
const ROOM_STATUS_MAX_ATTEMPTS = 3;
/** Base backoff between room-status retries, in ms. */
const ROOM_STATUS_RETRY_DELAY_MS = 250;

/** Resolve after `ms` milliseconds. */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Get room session if valid (returns session details without passcode verification - for public display)
// Uses server-side API to bypass RLS restrictions on completed/expired sessions.
//
// Transient 5xx responses and network rejections are retried with a short
// backoff. If every attempt fails the caller gets `{ exists: false,
// serverError: true }` so it can distinguish "could not verify, try again"
// from a genuine 200 "this room does not exist" (A10).
export async function getRoomPublicInfo(
  roomId: string,
): Promise<RoomPublicInfo | null> {
  let lastError: unknown = null;

  for (let attempt = 1; attempt <= ROOM_STATUS_MAX_ATTEMPTS; attempt++) {
    try {
      const response = await fetch('/api/room/status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ room_id: roomId }),
      });

      // Transient server errors (5xx) are retryable: the room may well exist,
      // the status service is just unavailable. Retry, then fail closed as a
      // server error rather than as a missing room.
      if (response.status >= 500) {
        lastError = new Error(`Room status check returned ${response.status}`);
        if (attempt < ROOM_STATUS_MAX_ATTEMPTS) {
          await delay(ROOM_STATUS_RETRY_DELAY_MS * attempt);
          continue;
        }
        captureException(
          lastError instanceof Error ? lastError : new Error(String(lastError)),
          'roomValidation.statusServerError',
          { roomId, statusCode: response.status },
        );
        return { exists: false, serverError: true };
      }

      // Non-5xx, non-ok (e.g. 4xx) is treated as a definitive "no room" per
      // the enumeration-safe API contract (the real endpoint always 200s for
      // existence questions, so a 4xx is a genuine rejection, not transient).
      if (!response.ok) {
        return { exists: false };
      }

      const raw = (await response.json()) as Record<string, unknown>;
      return {
        exists: !!raw.exists,
        status: raw.status as string | undefined,
        targetName: raw.targetName as string | undefined,
        interviewLanguage: raw.interviewLanguage as string | undefined,
        hasStarted: raw.hasStarted as boolean | undefined,
        workspaceId: (raw.workspace_id as string | null | undefined) ?? null,
        workspaceLogoUrl:
          (raw.workspace_logo_url as string | null | undefined) ?? null,
        workspaceAccentColor: typeof raw.workspace_accent_color === 'string' ? raw.workspace_accent_color : null,
        workspaceIconUrl:
          (raw.workspace_icon_url as string | null | undefined) ?? null,
      };
    } catch (error) {
      // Network reject: retryable, same as a 5xx.
      lastError = error;
      if (attempt < ROOM_STATUS_MAX_ATTEMPTS) {
        await delay(ROOM_STATUS_RETRY_DELAY_MS * attempt);
        continue;
      }
    }
  }

  console.error('Error checking room status:', lastError);
  captureException(
    lastError instanceof Error ? lastError : new Error(String(lastError)),
    'roomValidation.statusCheckError',
    { roomId },
  );
  return { exists: false, serverError: true };
}

export default {
  validateRoomAccess,
  validateRoomAccessByHash,
  getRoomPublicInfo,
};
